/**
 * Protest-Workflow — Konstanten & Display-Helper
 *
 * Wettbewerbsordnung § 15: Protest-Mechanismus mit 250 EUR Note,
 * sportrechtlicher Standard fuer Rueckerstattung.
 */

import type { ProtestStatus, ProtestDecision } from '@prisma/client'

// ── Geld ───────────────────────────────────────────────────────────────────
export const PROTEST_NOTE_AMOUNT = 250 // EUR
export const PROTEST_CURRENCY = 'EUR'

// ── Anhang-Limits ──────────────────────────────────────────────────────────
export const PROTEST_MAX_ATTACHMENTS = 5
export const PROTEST_MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024 // 25 MB pro Datei
export const PROTEST_ALLOWED_MIME_TYPES = [
  // Bilder
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  // Videos (klein gehaltene Demos / Mobile-Captures)
  'video/mp4',
  'video/webm',
  'video/quicktime', // .mov vom iPhone
] as const

// ── Wettbewerbsordnung-Paragraphen (zur Multi-Select-Auswahl) ─────────────
// Quelle: src/app/wettbewerbsordnung/page.tsx (§ 0 .. § 22)
export interface ProtestParagraphOption {
  num: string       // "§ 7"
  numDisplay: string // "07"
  title: string
}

export const PROTEST_PARAGRAPH_OPTIONS: ProtestParagraphOption[] = [
  { num: '§ 0',  numDisplay: '00', title: 'Verhältnis zu den Nutzungsbedingungen' },
  { num: '§ 1',  numDisplay: '01', title: 'Charakter der Serie — FUN & Förderung' },
  { num: '§ 2',  numDisplay: '02', title: 'Veranstalterrolle' },
  { num: '§ 3',  numDisplay: '03', title: 'Teilnahmeberechtigung' },
  { num: '§ 4',  numDisplay: '04', title: 'Saison, Einreichungsfristen, UTC' },
  { num: '§ 5',  numDisplay: '05', title: 'Internationale Geltung' },
  { num: '§ 6',  numDisplay: '06', title: 'Eigenverantwortung der Pilotinnen und Piloten' },
  { num: '§ 7',  numDisplay: '07', title: 'Wertungsklassen und Zeitwertungen' },
  { num: '§ 8',  numDisplay: '08', title: 'Geländearten — Flachland & Hang' },
  { num: '§ 9',  numDisplay: '09', title: 'Drei-Finger-Regel — Übersicht' },
  { num: '§ 10', numDisplay: '10', title: 'Start (2.1) — Startfenster, Startarten, Startzeitpunkt' },
  { num: '§ 11', numDisplay: '11', title: 'Wertungsflug (2.2) — Höhe, Motor, Multistart' },
  { num: '§ 12', numDisplay: '12', title: 'Flugende (2.3) — Landung & Einreichungsfrist' },
  { num: '§ 13', numDisplay: '13', title: 'Zugelassene IGC-Logger' },
  { num: '§ 14', numDisplay: '14', title: 'Einreichung der Streckenflüge & Wertungslogik' },
  { num: '§ 15', numDisplay: '15', title: 'Wertung & Auswertung (Protestverfahren)' },
  { num: '§ 16', numDisplay: '16', title: 'Pokale, Auszeichnungen, Preise' },
  { num: '§ 17', numDisplay: '17', title: 'Wahrheitspflicht & Prüfungsbefugnis' },
  { num: '§ 18', numDisplay: '18', title: 'Ausschlussgründe' },
  { num: '§ 19', numDisplay: '19', title: 'Verhaltensregeln & Sanktionen' },
  { num: '§ 20', numDisplay: '20', title: 'Veröffentlichung, Datenschutz & Bewegungsdaten' },
  { num: '§ 21', numDisplay: '21', title: 'Änderungen der Wettbewerbsordnung' },
  { num: '§ 22', numDisplay: '22', title: 'Anwendbares Recht & Gerichtsstand' },
]

const VALID_PARAGRAPHS = new Set(PROTEST_PARAGRAPH_OPTIONS.map((p) => p.num))

export function isValidParagraph(num: string): boolean {
  return VALID_PARAGRAPHS.has(num)
}

export function sanitizeParagraphList(input: unknown): string[] {
  if (!Array.isArray(input)) return []
  const cleaned = new Set<string>()
  for (const item of input) {
    if (typeof item === 'string' && isValidParagraph(item)) {
      cleaned.add(item)
    }
  }
  return Array.from(cleaned)
}

// ── Status-/Decision-Display-Mapping (Public + intern) ─────────────────────
//
// Mapping nach User-Anweisung:
//   PENDING_PAYMENT/PAID/UNDER_REVIEW   → "In Prüfung"  (Public)
//   DECIDED + GRANTED                    → "Zugestimmt"
//   DECIDED + PARTIALLY_GRANTED          → "Teilberechtigt"
//   DECIDED + REJECTED                   → "Abgelehnt"
//   DECIDED + DISMISSED                  → "Unberechtigt"
//   REFUNDED                             → bleibt bei Decision-Label

export type ProtestPublicStatus =
  | 'pending'        // wartet auf Zahlung — NICHT öffentlich anzeigen
  | 'in_review'      // In Prüfung
  | 'granted'        // Zugestimmt
  | 'partially'      // Teilberechtigt
  | 'rejected'       // Abgelehnt
  | 'dismissed'      // Unberechtigt
  | 'cancelled'      // Zurückgezogen — NICHT öffentlich anzeigen

export function publicStatusOf(status: ProtestStatus, decision: ProtestDecision | null): ProtestPublicStatus {
  if (status === 'PENDING_PAYMENT') return 'pending'
  if (status === 'CANCELLED') return 'cancelled'
  if (status === 'PAID' || status === 'UNDER_REVIEW') return 'in_review'
  // DECIDED oder REFUNDED — Decision bestimmt Label
  if (decision === 'GRANTED') return 'granted'
  if (decision === 'PARTIALLY_GRANTED') return 'partially'
  if (decision === 'REJECTED') return 'rejected'
  if (decision === 'DISMISSED') return 'dismissed'
  return 'in_review' // Fallback
}

export const PUBLIC_STATUS_LABEL_DE: Record<ProtestPublicStatus, string> = {
  pending: 'Wartet auf Zahlung',
  in_review: 'In Prüfung',
  granted: 'Zugestimmt',
  partially: 'Teilberechtigt',
  rejected: 'Abgelehnt',
  dismissed: 'Unberechtigt',
  cancelled: 'Zurückgezogen',
}

// Farb-Tokens fuer UI-Badges (Tailwind-kompatibel)
export const PUBLIC_STATUS_COLOR: Record<ProtestPublicStatus, { bg: string; fg: string }> = {
  pending:    { bg: 'rgba(180,180,180,0.15)', fg: '#aaa' },
  in_review:  { bg: 'rgba(242,201,78,0.15)',  fg: '#f2c94e' }, // gold
  granted:    { bg: 'rgba(46,204,113,0.18)',  fg: '#2ecc71' }, // grün
  partially:  { bg: 'rgba(241,196,15,0.18)',  fg: '#f1c40f' }, // gelb
  rejected:   { bg: 'rgba(231,76,60,0.15)',   fg: '#e74c3c' }, // rot
  dismissed:  { bg: 'rgba(149,165,166,0.18)', fg: '#95a5a6' }, // grau
  cancelled:  { bg: 'rgba(120,120,120,0.15)', fg: '#888' },
}

/**
 * Berechnet die Refund-Hoehe basierend auf Decision + Quote.
 * GRANTED  -> 100% von paid_amount
 * PARTIALLY_GRANTED -> quote% von paid_amount
 * Sonst    -> 0
 */
export function calculateRefundAmount(
  decision: ProtestDecision,
  paidAmount: number,
  quote: number | null,
): number {
  if (decision === 'GRANTED') return paidAmount
  if (decision === 'PARTIALLY_GRANTED' && quote !== null) {
    return Math.round((paidAmount * quote) / 100 * 100) / 100 // auf 2 Nachkommastellen
  }
  return 0
}
